import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyBookings } from "../api";
import { useLanguage } from "../LanguageContext";
import { formatScheduled, formatRelative } from "../utils/dateFormat";

interface Booking {
  bookingId: string;
  serviceType: string;
  scheduledDate: string;
  proName: string;
  status: string;
  address: string;
  proId?: string;
  category?: string;
  createdAt?: string;
  quoteAmount?: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  confirmed:  { label: "Confirmed",  color: "text-teal",        bg: "bg-teal-light",   dot: "bg-teal" },
  pending:    { label: "Pending",    color: "text-yellow-600",  bg: "bg-yellow-50",    dot: "bg-yellow-400" },
  completed:  { label: "Completed",  color: "text-gray-400",    bg: "bg-gray-100",     dot: "bg-gray-400" },
  declined:   { label: "Declined",   color: "text-red-500",     bg: "bg-red-50",       dot: "bg-red-400" },
  dispatched: { label: "Searching",  color: "text-mouna-blue",  bg: "bg-blue-pale",    dot: "bg-mouna-blue" },
  cancelled:  { label: "Cancelled",  color: "text-gray-400",    bg: "bg-gray-100",     dot: "bg-gray-400" },
};

const CATEGORY_ICONS: Record<string, string> = {
  Electrical: "⚡", Cleaning: "🧹", Plumbing: "🔧",
  Outdoor: "🌿", Painting: "🎨", Moving: "🚚",
  Carpentry: "🔨", "Interior Design": "🎭", General: "🏠",
};

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Active" },
  { key: "completed", label: "Done" },
];

export default function BookingsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    getMyBookings()
      .then(data => setBookings(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = bookings
    .filter(b => activeTab === "all" || b.status === activeTab ||
      (activeTab === "pending" && b.status === "dispatched"))
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  const counts = {
    all: bookings.length,
    pending: bookings.filter(b => b.status === "pending" || b.status === "dispatched").length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    completed: bookings.filter(b => b.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-0 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-navy">My Bookings</h1>
            <p className="text-xs text-gray-400 mt-0.5">{bookings.length} total bookings</p>
          </div>
          <button onClick={() => navigate("/explore")}
            className="bg-mouna-blue text-white text-xs font-bold rounded-full px-4 py-2">
            + Book Pro
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={"flex-1 py-3 text-xs font-bold transition-all relative " +
                (activeTab === tab.key ? "text-mouna-blue" : "text-gray-400")}>
              {tab.label}
              {counts[tab.key as keyof typeof counts] > 0 && (
                <span className={"ml-1 text-xs rounded-full px-1.5 py-0.5 " +
                  (activeTab === tab.key ? "bg-mouna-blue text-white" : "bg-gray-100 text-gray-400")}>
                  {counts[tab.key as keyof typeof counts]}
                </span>
              )}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-mouna-blue rounded-full"/>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="skeleton h-5 w-20 rounded-full"></div>
                  <div className="skeleton h-4 w-16 rounded"></div>
                </div>
                <div className="skeleton h-5 w-3/4 rounded mb-2"></div>
                <div className="skeleton h-3 w-1/2 rounded mb-1"></div>
                <div className="skeleton h-3 w-2/3 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 animate-fade-in">
            <div className="w-20 h-20 bg-blue-pale rounded-full flex items-center justify-center text-4xl mx-auto mb-4">📅</div>
            <div className="font-bold text-navy text-lg mb-2">
              {activeTab === "all" ? "No bookings yet" : `No ${activeTab} bookings`}
            </div>
            <div className="text-sm text-gray-400 mb-6">
              {activeTab === "all" ? "Book a professional to get started" : "Nothing here right now"}
            </div>
            <button onClick={() => navigate("/explore")}
              className="btn btn-primary">
              Find a Professional
            </button>
          </div>
        )}

        {/* Booking cards */}
        {!loading && filtered.map(booking => {
          const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
          const icon = CATEGORY_ICONS[booking.category || ""] || "🏠";

          return (
            <div key={booking.bookingId}
              className="bg-white rounded-2xl shadow-card mb-3 overflow-hidden animate-fade-in">
              {/* Status bar */}
              <div className={`px-4 py-2.5 flex items-center justify-between ${status.bg}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${status.dot}`}></div>
                  <span className={`text-xs font-bold ${status.color}`}>{status.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {booking.status === "confirmed" && (
                    <button onClick={() => navigate(`/help?bookingId=${booking.bookingId}&proId=${booking.proId}`)}
                      className="text-xs text-mouna-blue font-bold bg-white rounded-full px-3 py-1">
                      Get Help
                    </button>
                  )}
                  {booking.createdAt && (
                    <span className="text-xs text-gray-400">{formatRelative(booking.createdAt)}</span>
                  )}
                </div>
              </div>

              {/* Card body */}
              <div className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-pale rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                    {icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-navy text-sm leading-tight mb-1">
                      {booking.serviceType}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      📅 {formatScheduled(booking.scheduledDate)}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      📍 {booking.address}
                    </div>
                  </div>
                </div>

                {/* Pro's quote — only when set */}
                {booking.quoteAmount && booking.quoteAmount > 0 ? (
                  <div className="bg-teal-light rounded-xl px-3 py-2 mb-3 flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-500">Pro's quote</span>
                    <span className="text-base font-bold text-teal">${booking.quoteAmount}</span>
                  </div>
                ) : null}

                {/* Pro info */}
                {booking.proName && (
                  <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-xs font-bold">
                        {booking.proName.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-navy">{booking.proName}</div>
                        <div className="text-xs text-gray-400">Service Provider</div>
                      </div>
                    </div>
                    {booking.status === "confirmed" && booking.proId && (
                      <button
                        onClick={() => navigate(`/messages?conversation=${(() => { const k = Object.keys(localStorage).find(key => key.includes("idToken")); if (!k) return "client"; const t = localStorage.getItem(k); if (!t) return "client"; try { return JSON.parse(atob(t.split(".")[1])).sub; } catch { return "client"; } })()}_${booking.proId}&recipient=${booking.proId}&name=${booking.proName}`)}
                        className="text-xs text-mouna-blue font-bold bg-blue-pale rounded-full px-3 py-1.5">
                        💬 Message
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around py-2 px-4 z-30">
        {[
          { icon: "🏠", label: t("home"), path: "/home" },
          { icon: "🔍", label: t("explore"), path: "/explore" },
          { icon: "📅", label: t("bookings"), path: "/bookings" },
          { icon: "👤", label: t("profile"), path: "/profile" },
        ].map(item => (
          <button key={item.path} onClick={() => navigate(item.path)}
            className="flex flex-col items-center gap-1 px-3 py-1">
            <span className="text-xl">{item.icon}</span>
            <span className={`text-xs font-semibold ${window.location.pathname === item.path ? "text-mouna-blue" : "text-gray-400"}`}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
